import { listedConnectorUsageTypes } from "../../shared/connectorUsageReporting";
import { usageTypeIcons } from "../lib/connector";
import { pluralize } from "../lib/inflection";
import { ConnectorLogoContainer } from "./ConnectorLogoContainer";
import { FacePile } from "./FacePile";
import { Icon, type IconName } from "./Icon";
import { Tooltip } from "./Tooltip";

const usageNouns: Record<(typeof listedConnectorUsageTypes)[number], string> = {
  personal_chat: "chat",
  space_git_remote: "git sync",
  audit_log_destination: "audit log destination",
  ai_provider: "AI provider",
};

export function ConnectorUsageIcons({
  workflowCount,
  usages,
  collapseToFit = false,
}: {
  // Workflows with a live branch head running the connector.
  workflowCount: number;
  usages: readonly { type: string }[];
  collapseToFit?: boolean;
}) {
  const counts = new Map<string, number>();
  for (const usage of usages)
    counts.set(usage.type, (counts.get(usage.type) ?? 0) + 1);

  const all: { id: string; icon: IconName; count: number; noun: string }[] = [
    {
      id: "workflows",
      icon: usageTypeIcons.workflow,
      count: workflowCount,
      noun: "workflow",
    },
    ...listedConnectorUsageTypes.map((type) => ({
      id: type,
      icon: usageTypeIcons[type],
      count: counts.get(type) ?? 0,
      noun: usageNouns[type],
    })),
  ];
  const entries = all.filter((entry) => entry.count > 0);
  if (entries.length === 0) return null;

  return (
    <FacePile
      itemSize={24}
      outlineWidth={2}
      cutout
      collapseToFit={collapseToFit}
      items={entries.map((entry) => {
        const label = `${entry.count} ${pluralize(entry.noun, entry.count)}`;
        return {
          id: entry.id,
          label,
          bare: true,
          node: (
            <Tooltip content={label}>
              <ConnectorLogoContainer
                size={24}
                outline={false}
                outlineWidth={2}
              >
                <Icon
                  name={entry.icon}
                  size={12}
                  color="var(--text-secondary)"
                />
              </ConnectorLogoContainer>
            </Tooltip>
          ),
        };
      })}
    />
  );
}
